import { computeSpaces, spaceContains } from './spaces.jsx';
import { expandArcs } from './arcWalls.jsx';
import { spaceEdges, edgeLoops } from '../element3D/spacePieces.jsx';

// «مرزبندیِ هوشمند»: کاربر اسمِ یک قسمت (مثلاً پذیرایی) و — اختیاری — مساحتش را می‌دهد و یک نقطه داخلش را می‌زند.
// این‌جا یک مرزِ صاف (افقی یا عمودی، از دیوار تا دیوار) پیدا می‌شود که فضای آن نقطه را طوری جدا کند که قسمتِ شاملِ
// نقطه همان مساحت را داشته باشد. چهار حالت امتحان می‌شود (مرزِ عمودی چپ/راستِ نقطه، افقی بالا/پایینِ نقطه) و
// بهترین برمی‌گردد: مساحتِ نزدیک‌تر، مرزِ کوتاه‌تر، قسمتِ جمع‌وجورتر و نقطه نزدیک‌ترِ به وسطِ قسمت.
// اگر با یک خطِ صاف نشد (مثلاً نقطه وسطِ یک سالنِ بزرگ است و قسمتِ کوچکی می‌خواهد)، یک قسمتِ مستطیلی در گوشه‌ی فضا
// با دو مرز (L شکل) هم امتحان می‌شود.
// مختصات سانتی‌متر. خروجی: { segs: [{x1,y1,x2,y2}], area } | { whole: true, area } | { error }

const piecesOf = (sp) => (sp.rects && sp.rects.length ? sp.rects : [sp]);
const isTrap = (q) => q.xl0 !== undefined;
const xl = (q, t) => (isTrap(q) ? q.xl0 + (q.xl1 - q.xl0) * t : q.x1);
const xr = (q, t) => (isTrap(q) ? q.xr0 + (q.xr1 - q.xr0) * t : q.x2);

// مساحتِ بخشی از فضا که x < c (axis 'x') یا y < c (axis 'y') است
function areaBelow(pieces, axis, c) {
  let a = 0;
  pieces.forEach((q) => {
    const h = q.y2 - q.y1;
    if (h <= 0) return;
    if (axis === 'y') {
      if (c <= q.y1) return;
      const t1 = Math.min(1, (c - q.y1) / h);
      // پهنا خطی است → ذوزنقه
      const w0 = xr(q, 0) - xl(q, 0);
      const w1 = xr(q, t1) - xl(q, t1);
      a += ((w0 + w1) / 2) * t1 * h;
      return;
    }
    const N = isTrap(q) ? 16 : 1;
    for (let k = 0; k < N; k += 1) {
      const t = (k + 0.5) / N;
      const w = Math.max(0, Math.min(xr(q, t), c) - xl(q, t));
      a += (w * h) / N;
    }
  });
  return a;
}

// پرتو از p در امتدادِ محور تا اولین دیوار
function castRay(segs, px, py, axis, dir) {
  let best = Infinity;
  for (const s of segs) {
    const dx = axis === 'x' ? dir : 0;
    const dy = axis === 'y' ? dir : 0;
    const ex = s.x2 - s.x1;
    const ey = s.y2 - s.y1;
    const den = dx * ey - dy * ex;
    const qx = s.x1 - px;
    const qy = s.y1 - py;
    if (Math.abs(den) < 1e-9) {
      // هم‌راستا: دیوارِ جلوی پرتو روی همان خط → تا سرِ نزدیکش (روی خطِ دیوار نکشد)
      if (Math.abs(qx * dy - qy * dx) > 1) continue;
      const t1 = qx * dx + qy * dy;
      const t2 = (s.x2 - px) * dx + (s.y2 - py) * dy;
      const tn = Math.min(t1, t2);
      if (Math.max(t1, t2) > 0.5 && tn <= 0.5) return 0; // نقطه خودش روی دیوار است
      if (tn > 0.5 && tn < best) best = tn;
      continue;
    }
    const t = (qx * ey - qy * ex) / den;
    const u = (qx * dy - qy * dx) / den;
    if (u < -0.002 || u > 1.002) continue;
    if (t > 0.5 && t < best) best = t;
  }
  return Number.isFinite(best) ? best : null;
}

// فاصله از نقطه تا اولین دیوار در امتدادِ محور (برای کشیدنِ مرز) — null اگر نرسید یا نقطه روی دیوار است
export function rayToWall(walls, px, py, axis, dir) {
  const t = castRay(expandArcs(walls), px, py, axis, dir);
  return t ? t : null;
}

const withIds = (ws) => ws.map((w, i) => ({ id: w.id ?? `t${i}`, ...w }));

export function findSpaceAt(walls, x, y) {
  const sps = computeSpaces(withIds(walls), [], [], []);
  return { spaces: sps, space: sps.find((s) => spaceContains(s, x, y)) || null };
}

export function proposeZoneCut(walls, P, target, opts = {}) {
  // مساحتی که خودِ کاربر نوشت مهم‌تر از تخمینِ برنامه است
  const areaW = opts.explicit ? 1 : 0.4;
  const { space: S } = findSpaceAt(walls, P.x, P.y);
  if (!S) return { error: 'outside' };
  if (target >= S.area * 0.9) return { whole: true, area: S.area };
  const pieces = piecesOf(S);
  const minX = Math.min(...pieces.map((q) => Math.min(xl(q, 0), xl(q, 1))));
  const maxX = Math.max(...pieces.map((q) => Math.max(xr(q, 0), xr(q, 1))));
  const minY = Math.min(...pieces.map((q) => q.y1));
  const maxY = Math.max(...pieces.map((q) => q.y2));
  const segs = expandArcs(walls);
  const margin = 40;
  const cands = [];
  // سرِ دیوارها داخلِ محدوده‌ی همین فضا
  const feats = segs
    .flatMap((q) => [
      [q.x1, q.y1],
      [q.x2, q.y2],
    ])
    .filter(([x, y]) => x > minX + 5 && x < maxX - 5 && y > minY + 5 && y < maxY - 5);

  ['x', 'y'].forEach((axis) => {
    const lo = axis === 'x' ? minX : minY;
    const hi = axis === 'x' ? maxX : maxY;
    const p = axis === 'x' ? P.x : P.y;
    [-1, 1].forEach((side) => {
      // side −۱: قسمتِ نقطه پایینِ c (مرز بعد از نقطه)؛ +۱: بالای c
      const part = (c) => (side < 0 ? areaBelow(pieces, axis, c) : S.area - areaBelow(pieces, axis, c));
      let a = side < 0 ? p + margin : lo;
      let b = side < 0 ? hi : p - margin;
      if (b - a < 10) return;
      // part(c) یکنواخت است: side −۱ صعودی، +۱ نزولی
      const fa = part(a);
      const fb = part(b);
      let c;
      if ((target - fa) * (target - fb) > 0) {
        // این طرف دقیقاً به این مساحت نمی‌رسد — نزدیک‌ترین (امتیازش با اختلافِ مساحت کم می‌شود)
        c = Math.round(Math.abs(fa - target) < Math.abs(fb - target) ? a : b);
      } else {
        for (let it = 0; it < 40; it += 1) {
          const m = (a + b) / 2;
          const fm = part(m);
          if ((side < 0 && fm < target) || (side > 0 && fm > target)) a = m;
          else b = m;
        }
        c = Math.round((a + b) / 2);
      }
      tryLine(axis, c, 0);
      // هم‌راستا با سرِ دیوار/گوشه‌ای در همین نزدیکی (مرزِ طبیعی‌تر) — اگر مساحت زیاد عوض نشود
      feats
        .map((v) => (axis === 'x' ? v[0] : v[1]))
        .filter((v) => (side < 0 ? v > p + margin && v < hi - margin : v < p - margin && v > lo + margin))
        .filter((v, k, arr) => arr.indexOf(v) === k && Math.abs(v - c) < Math.sqrt(target) * 0.5)
        .slice(0, 8)
        .forEach((v) => tryLine(axis, v, -0.15));
    });
  });

  // یک مرزِ صاف روی خطِ c (عمود بر محورِ axis)، از دیوار تا دیوار، از نزدیک‌ترین جا به نقطه‌ی کاربر
  function tryLine(axis, c, bonus) {
      // نقطه‌ی شروعِ پرتو روی خطِ c، داخلِ فضا و نزدیکِ نقطه‌ی کاربر
      const o = axis === 'x' ? P.y : P.x;
      let start = null;
      pieces.forEach((q) => {
        let v;
        if (axis === 'x') {
          if (c <= Math.min(xl(q, 0), xl(q, 1)) || c >= Math.max(xr(q, 0), xr(q, 1))) return;
          v = Math.max(q.y1 + 1, Math.min(q.y2 - 1, o));
          const t = (v - q.y1) / (q.y2 - q.y1 || 1);
          if (c <= xl(q, t) || c >= xr(q, t)) return;
        } else {
          if (c <= q.y1 || c >= q.y2) return;
          const t = (c - q.y1) / (q.y2 - q.y1 || 1);
          v = Math.max(xl(q, t) + 1, Math.min(xr(q, t) - 1, o));
        }
        if (!start || Math.abs(v - o) < Math.abs(start - o)) start = v;
      });
      if (start == null) return;
      const sx = axis === 'x' ? c : start;
      const sy = axis === 'x' ? start : c;
      const along = axis === 'x' ? 'y' : 'x';
      const tn = castRay(segs, sx, sy, along, -1);
      const tp = castRay(segs, sx, sy, along, 1);
      if (!tn || !tp) return;
      const seg =
        axis === 'x'
          ? { x1: c, y1: Math.round(sy - tn), x2: c, y2: Math.round(sy + tp) }
          : { x1: Math.round(sx - tn), y1: c, x2: Math.round(sx + tp), y2: c };
      const ev = evaluate([seg]);
      if (ev) cands.push({ ...ev, score: ev.score + bonus });
  }

  // قسمتِ مستطیلی در هر گوشه (دو مرزِ L شکل)
  [
    [minX, minY, 1, 1],
    [maxX, minY, -1, 1],
    [minX, maxY, 1, -1],
    [maxX, maxY, -1, -1],
  ].forEach(([cx, cy, sx, sy]) => {
    const wMin = Math.abs(P.x - cx) + margin;
    const hMin = Math.abs(P.y - cy) + margin;
    let tgt = target;
    let best = null;
    for (let it = 0; it < 3; it += 1) {
      let w = Math.sqrt(tgt);
      let h = w;
      if (w < wMin) {
        w = wMin;
        h = tgt / w;
      }
      if (h < hMin) {
        h = hMin;
        w = Math.max(wMin, tgt / h);
      }
      if (w > maxX - minX - margin || h > maxY - minY - margin) return;
      const qx = Math.round(cx + sx * w);
      const qy = Math.round(cy + sy * h);
      if (!spaceContains(S, qx, qy)) return;
      const tv = castRay(segs, qx, qy, 'y', -sy);
      const th = castRay(segs, qx, qy, 'x', -sx);
      if (tv == null || th == null) return;
      const ev = evaluate([
        { x1: qx, y1: qy, x2: qx, y2: Math.round(qy - sy * tv) },
        { x1: qx, y1: qy, x2: Math.round(qx - sx * th), y2: qy },
      ]);
      if (!ev) return;
      best = ev;
      if (Math.abs(ev.area - target) < target * 0.02) break;
      tgt *= target / ev.area;
    }
    if (best) cands.push({ ...best, score: best.score + 0.1 }); // یک خطِ صاف را کمی ترجیح بده
  });

  function evaluate(list) {
      // مساحتِ واقعی با همین مرز(ها)
      const { space: R } = findSpaceAt([...walls, ...list.map((q) => ({ ...q, virtual: true }))], P.x, P.y);
      if (!R || R.area >= S.area * 0.97) return null;
      const len = list.reduce((sum, q) => sum + Math.hypot(q.x2 - q.x1, q.y2 - q.y1), 0);
      const rp = piecesOf(R);
      const bx0 = Math.min(...rp.map((q) => q.x1));
      const bx1 = Math.max(...rp.map((q) => q.x2));
      const by0 = Math.min(...rp.map((q) => q.y1));
      const by1 = Math.max(...rp.map((q) => q.y2));
      const bw = bx1 - bx0;
      const bh = by1 - by0;
      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      const off = Math.hypot(P.x - (bx0 + bx1) / 2, P.y - (by0 + by1) / 2) / Math.sqrt(R.area);
      const score =
        (areaW * Math.abs(R.area - target)) / target + // مساحت
        0.35 * (len / Math.sqrt(target)) + // مرزِ کوتاه
        0.15 * Math.max(0, aspect - 1.6) + // قسمتِ کشیده نه
        0.5 * off; // نقطه وسطِ قسمت
      return { segs: list, area: R.area, score };
  }
  if (!cands.length) return { error: 'noCut' };
  cands.sort((p, q) => p.score - q.score);
  return { segs: cands[0].segs, area: cands[0].area };
}

// شکلِ یک فضا برای کشیدن (تکه‌ها به‌صورتِ چندضلعی)
export function spacePolys(sp) {
  return piecesOf(sp).map((q) =>
    isTrap(q)
      ? [q.xl0, q.y1, q.xr0, q.y1, q.xr1, q.y2, q.xl1, q.y2]
      : [q.x1, q.y1, q.x2, q.y1, q.x2, q.y2, q.x1, q.y2]
  );
}

// ---------- قسمتِ «محیطِ بسته» (چندضلعیِ خط‌چین) ----------
// هر قسمتِ مرزبندی‌شده یک چندضلعیِ بسته است: [[x, y], ...]. کاربر هر نقطه‌اش را می‌گیرد و می‌کشد و بقیه‌ی خط
// نرم و متناسب دنبالش می‌آید (هرچه دورتر، کمتر). شکل هرچه شود (دایره، مثلث، مستطیل...) همیشه بسته است. در پلان
// به «مرزِ فضا»ی معمولی (دیوارِ virtual با zoneOf) تبدیل می‌شود — جاهایی که روی دیوارِ واقعی است حذف.


export function polyArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

export function polyCentroid(poly) {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    const f = x1 * y2 - x2 * y1;
    a += f;
    cx += (x1 + x2) * f;
    cy += (y1 + y2) * f;
  }
  if (Math.abs(a) < 1e-6) return { x: poly[0][0], y: poly[0][1] };
  return { x: cx / (3 * a), y: cy / (3 * a) };
}

export function pointInPoly(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// یک نقطه‌ی حتماً داخلِ چندضلعی (برای پیدا کردنِ فضای همین قسمت)
export function polyInterior(poly, hint) {
  const c = polyCentroid(poly);
  if (pointInPoly(poly, c.x, c.y)) return c;
  if (hint && pointInPoly(poly, hint.x, hint.y)) return hint;
  // وسطِ یک تکه‌ی افقی داخلِ شکل
  const ys = poly.map((p) => p[1]);
  const y = (Math.min(...ys) + Math.max(...ys)) / 2 + 0.37;
  const xs = [];
  for (let i = 0; i < poly.length; i += 1) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    if (y1 > y !== y2 > y) xs.push(x1 + ((y - y1) * (x2 - x1)) / (y2 - y1));
  }
  xs.sort((p, q) => p - q);
  return xs.length >= 2 ? { x: (xs[0] + xs[1]) / 2, y } : c;
}

// دورِ فضای شاملِ P (بزرگ‌ترین حلقه‌ی بسته)
export function regionPolygon(walls, P) {
  const { space } = findSpaceAt(walls, P.x, P.y);
  if (!space) return null;
  const loops = edgeLoops(spaceEdges(piecesOf(space))).filter((l) => l.closed && l.segs.length >= 3);
  if (!loops.length) return null;
  const polys = loops.map((l) => l.segs.map((s) => [s.p[0], s.p[1]]));
  polys.sort((p, q) => polyArea(q) - polyArea(p));
  return polys[0];
}

// لبه‌های بلند به تکه‌های کوتاه (تا کشیدن نرم باشد) و حذفِ رأس‌های خیلی نزدیک؛ گوشه‌ها همان می‌مانند
export function resamplePoly(poly, step = 30) {
  const out = [];
  for (let i = 0; i < poly.length; i += 1) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    const L = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(1, Math.round(L / step));
    for (let k = 0; k < n; k += 1) out.push([x1 + ((x2 - x1) * k) / n, y1 + ((y2 - y1) * k) / n]);
  }
  const clean = [];
  out.forEach((p) => {
    const last = clean[clean.length - 1];
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > step * 0.3) clean.push(p);
  });
  if (clean.length > 3 && Math.hypot(clean[0][0] - clean[clean.length - 1][0], clean[0][1] - clean[clean.length - 1][1]) < step * 0.3) clean.pop();
  return clean;
}

const perimeterPos = (poly) => {
  const s = [0];
  for (let i = 1; i < poly.length; i += 1) s.push(s[i - 1] + Math.hypot(poly[i][0] - poly[i - 1][0], poly[i][1] - poly[i - 1][1]));
  const total = s[s.length - 1] + Math.hypot(poly[0][0] - poly[poly.length - 1][0], poly[0][1] - poly[poly.length - 1][1]);
  return { s, total };
};

// نزدیک‌ترین جای خط به نقطه: رأسِ موجود (اگر نزدیک بود) یا یک رأسِ تازه همان‌جا. خروجی { poly, idx }
export function grabPoly(poly, x, y, snap) {
  let best = { d: Infinity };
  for (let i = 0; i < poly.length; i += 1) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const l2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / l2));
    const d = Math.hypot(x1 + dx * t - x, y1 + dy * t - y);
    if (d < best.d) best = { d, i, t, px: x1 + dx * t, py: y1 + dy * t, L: Math.sqrt(l2) };
  }
  const { i, t, L } = best;
  if (t * L <= snap) return { poly, idx: i };
  if ((1 - t) * L <= snap) return { poly, idx: (i + 1) % poly.length };
  const np = poly.slice();
  np.splice(i + 1, 0, [best.px, best.py]);
  return { poly: np, idx: i + 1 };
}

// کشیدنِ رأسِ idx به اندازه‌ی (dx, dy): بقیه با وزنِ گاوسی بر حسبِ فاصله روی خطِ دور (نه فاصله‌ی مستقیم)
// inside(x, y): آیا نقطه داخلِ خانه است — رأسی که بیرون می‌رود تا لبه‌ی خانه عقب کشیده می‌شود
export function deformPoly(poly0, idx, dx, dy, inside) {
  const { s, total } = perimeterPos(poly0);
  const sigma = Math.max(60, Math.min(260, total * 0.09));
  return poly0.map(([x, y], j) => {
    let ds = Math.abs(s[j] - s[idx]);
    ds = Math.min(ds, total - ds);
    const w = Math.exp(-((ds / sigma) ** 2));
    if (w < 0.004) return [x, y];
    let nx = x + dx * w;
    let ny = y + dy * w;
    if (inside && !inside(nx, ny)) {
      // آخرین جای مجاز روی مسیرِ همین رأس
      let lo = 0;
      let hi = 1;
      for (let it = 0; it < 14; it += 1) {
        const m = (lo + hi) / 2;
        if (inside(x + dx * w * m, y + dy * w * m)) lo = m;
        else hi = m;
      }
      nx = x + dx * w * lo;
      ny = y + dy * w * lo;
    }
    return [nx, ny];
  });
}

const distSegPt = (s, x, y) => {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((x - s.x1) * dx + (y - s.y1) * dy) / l2));
  return Math.hypot(s.x1 + dx * t - x, s.y1 + dy * t - y);
};

// ساده‌سازیِ حلقه (خطِ صاف = یک دیوار؛ قسمتِ خمیده چند تکه‌ی کوتاه)
export function simplifyClosed(poly, tol) {
  if (poly.length < 4) return poly;
  const keep = new Uint8Array(poly.length);
  // دو رأسِ دورترین از هم نقطه‌های ثابت
  let far = 0;
  poly.forEach((p, i) => {
    if (Math.hypot(p[0] - poly[0][0], p[1] - poly[0][1]) > Math.hypot(poly[far][0] - poly[0][0], poly[far][1] - poly[0][1])) far = i;
  });
  keep[0] = 1;
  keep[far] = 1;
  const rec = (a, b) => {
    // b ممکن است دورتر از آخرِ آرایه باشد (حلقه)
    const n = poly.length;
    const A = poly[a % n];
    const B = poly[b % n];
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
    let md = -1;
    let mi = -1;
    for (let k = a + 1; k < b; k += 1) {
      const P = poly[k % n];
      const d = Math.abs((P[0] - A[0]) * (B[1] - A[1]) - (P[1] - A[1]) * (B[0] - A[0])) / L;
      if (d > md) {
        md = d;
        mi = k;
      }
    }
    if (md > tol) {
      keep[mi % n] = 1;
      rec(a, mi);
      rec(mi, b);
    }
  };
  rec(0, far);
  rec(far, poly.length);
  return poly.filter((_, i) => keep[i]);
}

// چندضلعی → «مرزِ فضا»ها (فقط جاهایی که روی دیوارِ واقعی نیست)
export function polyToWalls(poly, realWalls, zoneId, nextId) {
  const segs = expandArcs(realWalls.filter((w) => !w.virtual));
  const pts = simplifyClosed(poly, 1.5);
  const onWall = (x, y) => segs.some((s) => distSegPt(s, x, y) <= 4);
  const out = [];
  for (let i = 0; i < pts.length; i += 1) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    if (Math.hypot(x2 - x1, y2 - y1) < 1) continue;
    if (onWall(x1, y1) && onWall(x2, y2) && onWall((x1 + x2) / 2, (y1 + y2) / 2)) continue;
    out.push({
      id: nextId(),
      x1: Math.round(x1 * 10) / 10,
      y1: Math.round(y1 * 10) / 10,
      x2: Math.round(x2 * 10) / 10,
      y2: Math.round(y2 * 10) / 10,
      virtual: true,
      zoneOf: zoneId,
    });
  }
  return out;
}

// ---------- چسبیدنِ خط‌چینِ قسمت (مثلِ سرِ دیوارها موقعِ کشیدن) ----------
// targets: { segs: پاره‌خط‌های دیوار/مرزها/خط‌چینِ قسمت‌های دیگر [{x1,y1,x2,y2}], xs, ys: امتدادِ دیوارها }
export function zoneSnapTargets(walls, rooms, zoneId) {
  const segs = expandArcs(walls.filter((w) => w.zoneOf !== zoneId)).map((w) => ({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 }));
  (rooms || []).forEach((r) => {
    if (r.id === zoneId || !r.zone || !r.zone.poly) return;
    const p = r.zone.poly;
    for (let i = 0; i < p.length; i += 1) {
      const a = p[i];
      const b = p[(i + 1) % p.length];
      segs.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1], zone: true });
    }
  });
  const xs = new Set();
  const ys = new Set();
  walls.forEach((w) => {
    if (w.zoneOf != null || w.arc) return;
    xs.add(w.x1);
    xs.add(w.x2);
    ys.add(w.y1);
    ys.add(w.y2);
  });
  return { segs, xs: [...xs], ys: [...ys] };
}

const projSeg = (s, x, y) => {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((x - s.x1) * dx + (y - s.y1) * dy) / l2));
  const px = s.x1 + dx * t;
  const py = s.y1 + dy * t;
  return { x: px, y: py, d: Math.hypot(px - x, py - y), t };
};

// نقطه‌ای که کاربر می‌کشد: اول سرِ دیوار/گوشه، بعد خودِ دیوار یا خط‌چینِ قسمتِ کناری، وگرنه هم‌راستا با امتدادِ
// دیوارها (حتی جایی که دیواری نیست). خروجی { x, y, guides: [{ x } | { y }], stuck }
export function snapZonePoint(x, y, T, tol) {
  // سرِ دیوارها
  let best = null;
  T.segs.forEach((s) => {
    [
      [s.x1, s.y1],
      [s.x2, s.y2],
    ].forEach(([ex, ey]) => {
      const d = Math.hypot(ex - x, ey - y);
      if (d <= tol && (!best || d < best.d)) best = { x: ex, y: ey, d };
    });
  });
  if (best) return { x: best.x, y: best.y, guides: [], stuck: true };
  // بدنه‌ی دیوار / خط‌چینِ کناری
  T.segs.forEach((s) => {
    const p = projSeg(s, x, y);
    if (p.d <= tol && (!best || p.d < best.d)) best = p;
  });
  if (best) return { x: best.x, y: best.y, guides: [], stuck: true };
  // امتدادِ دیوارها
  const guides = [];
  let nx = x;
  let ny = y;
  let bx = null;
  T.xs.forEach((v) => {
    const d = Math.abs(v - x);
    if (d <= tol && (bx == null || d < Math.abs(bx - x))) bx = v;
  });
  let by = null;
  T.ys.forEach((v) => {
    const d = Math.abs(v - y);
    if (d <= tol && (by == null || d < Math.abs(by - y))) by = v;
  });
  if (bx != null) {
    nx = bx;
    guides.push({ x: bx });
  }
  if (by != null) {
    ny = by;
    guides.push({ y: by });
  }
  return { x: nx, y: ny, guides, stuck: false };
}

// بقیه‌ی نقطه‌هایی که همراه آمده‌اند: اگر خیلی نزدیکِ دیوار/خط‌چینِ کناری شدند، روی آن بنشینند (فاصله‌ی ریز نماند)
export function stickPoly(poly, poly0, T, tol) {
  return poly.map(([x, y], j) => {
    if (Math.abs(x - poly0[j][0]) < 0.01 && Math.abs(y - poly0[j][1]) < 0.01) return [x, y];
    let best = null;
    T.segs.forEach((s) => {
      const p = projSeg(s, x, y);
      if (p.d <= tol && (!best || p.d < best.d)) best = p;
    });
    return best ? [best.x, best.y] : [x, y];
  });
}

// =====================================================================================================
// مرزِ صاف: «کشیدنِ یک خط با انگشت» برای ساختن، «دستگیره‌ی لبه و گوشه» برای ویرایش
// =====================================================================================================

// پرتو در جهتِ دلخواه (ux, uy) — نزدیک‌ترین برخورد با دیوار
function rayDir(segs, px, py, ux, uy) {
  let best = Infinity;
  for (const s of segs) {
    const ex = s.x2 - s.x1;
    const ey = s.y2 - s.y1;
    const den = ux * ey - uy * ex;
    if (Math.abs(den) < 1e-9) continue;
    const qx = s.x1 - px;
    const qy = s.y1 - py;
    const t = (qx * ey - qy * ex) / den;
    const u = (qx * uy - qy * ux) / den;
    if (u < -0.002 || u > 1.002) continue;
    if (t > 0.5 && t < best) best = t;
  }
  return Number.isFinite(best) ? best : null;
}

// تقاطعِ دو خط (نقطه + جهت) — null اگر موازی
function lineX(p, u, q, v) {
  const den = u[0] * v[1] - u[1] * v[0];
  if (Math.abs(den) < 1e-9) return null;
  const t = ((q[0] - p[0]) * v[1] - (q[1] - p[1]) * v[0]) / den;
  return [p[0] + u[0] * t, p[1] + u[1] * t];
}

const unit = (dx, dy) => {
  const L = Math.hypot(dx, dy) || 1;
  return [dx / L, dy / L];
};

// خطِ انگشت → خطِ شکسته‌ی صاف از دیوار تا دیوار: ساده‌سازی، زاویه‌ها روی ۰/۴۵/۹۰ درجه، دو سر تا دیوار
// stroke: [[x, y], ...] (سانتی‌متر). خروجی: [[x, y], ...] یا null
export function strokeToPolyline(stroke, walls, tol) {
  if (!stroke || stroke.length < 2) return null;
  let total = 0;
  for (let i = 1; i < stroke.length; i += 1) total += Math.hypot(stroke[i][0] - stroke[i - 1][0], stroke[i][1] - stroke[i - 1][1]);
  if (total < 40) return null;
  // داگلاس-پوکر روی خطِ باز
  const keep = new Uint8Array(stroke.length);
  keep[0] = 1;
  keep[stroke.length - 1] = 1;
  const rec = (a, b) => {
    const A = stroke[a];
    const B = stroke[b];
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
    let md = -1;
    let mi = -1;
    for (let k = a + 1; k < b; k += 1) {
      const P = stroke[k];
      const d = Math.abs((P[0] - A[0]) * (B[1] - A[1]) - (P[1] - A[1]) * (B[0] - A[0])) / L;
      if (d > md) {
        md = d;
        mi = k;
      }
    }
    if (md > tol) {
      keep[mi] = 1;
      rec(a, mi);
      rec(mi, b);
    }
  };
  rec(0, stroke.length - 1);
  const idx = [];
  keep.forEach((v, i) => {
    if (v) idx.push(i);
  });
  // هر تکه: جهت (چسبیده به ضربِ ۴۵ درجه) + نقطه‌ی میانی
  let parts = [];
  for (let k = 0; k + 1 < idx.length; k += 1) {
    const A = stroke[idx[k]];
    const B = stroke[idx[k + 1]];
    const len = Math.hypot(B[0] - A[0], B[1] - A[1]);
    if (len < 1) continue;
    let ang = Math.atan2(B[1] - A[1], B[0] - A[0]);
    const step = Math.PI / 4;
    const sn = Math.round(ang / step) * step;
    if (Math.abs(sn - ang) < (15 * Math.PI) / 180) ang = sn;
    // میانگینِ نقطه‌های همین تکه از خطِ انگشت
    let mx = 0;
    let my = 0;
    let n = 0;
    for (let j = idx[k]; j <= idx[k + 1]; j += 1) {
      mx += stroke[j][0];
      my += stroke[j][1];
      n += 1;
    }
    parts.push({ ang, len, p: [mx / n, my / n] });
  }
  // تکه‌های هم‌جهت یکی
  const merged = [];
  parts.forEach((q) => {
    const last = merged[merged.length - 1];
    let d = last ? Math.abs(q.ang - last.ang) % (2 * Math.PI) : 9;
    if (d > Math.PI) d = 2 * Math.PI - d;
    if (last && d < (25 * Math.PI) / 180) {
      const w = last.len + q.len;
      last.p = [(last.p[0] * last.len + q.p[0] * q.len) / w, (last.p[1] * last.len + q.p[1] * q.len) / w];
      last.ang = last.len >= q.len ? last.ang : q.ang;
      last.len = w;
    } else merged.push({ ...q });
  });
  // تکه‌های خیلی کوتاه (لرزشِ دست) حذف
  parts = merged.filter((q) => q.len >= Math.max(30, tol * 1.5) || merged.length === 1);
  if (!parts.length) return null;
  parts = parts.slice(0, 4);
  const lines = parts.map((q) => ({ p: q.p, u: [Math.cos(q.ang), Math.sin(q.ang)] }));
  // گوشه‌ها = تقاطعِ تکه‌های پشتِ‌سرِهم
  const corners = [];
  for (let k = 0; k + 1 < lines.length; k += 1) {
    const X = lineX(lines[k].p, lines[k].u, lines[k + 1].p, lines[k + 1].u);
    if (!X) return null;
    corners.push(X);
  }
  const segs = expandArcs(walls);
  // دو سر: از داخلِ خط به بیرون تا اولین دیوار
  const first = lines[0];
  const last = lines[lines.length - 1];
  const aFrom = corners.length ? corners[0] : first.p;
  const bFrom = corners.length ? corners[corners.length - 1] : last.p;
  const ta = rayDir(segs, aFrom[0], aFrom[1], -first.u[0], -first.u[1]);
  const tb = rayDir(segs, bFrom[0], bFrom[1], last.u[0], last.u[1]);
  if (ta == null || tb == null) return null;
  const A = [aFrom[0] - first.u[0] * ta, aFrom[1] - first.u[1] * ta];
  const B = [bFrom[0] + last.u[0] * tb, bFrom[1] + last.u[1] * tb];
  return [A, ...corners, B].map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
}

// کدام لبه‌ها روی دیوار (یا مرزِ دیگری) هستند — این‌ها ثابت‌اند؛ فقط لبه‌های آزاد دستگیره دارند
export function zoneEdgeFlags(poly, walls, zoneId) {
  const segs = expandArcs(walls.filter((w) => w.zoneOf !== zoneId));
  const on = (x, y) => segs.some((s) => distSegPt(s, x, y) <= 3);
  return poly.map((a, i) => {
    const b = poly[(i + 1) % poly.length];
    return [0.1, 0.5, 0.9].every((t) => on(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t));
  });
}

// لبه‌ی i (از رأسِ i تا i+1) به اندازه‌ی s در جهتِ عمودش؛ دو لبه‌ی کناری هم‌جهت می‌مانند و فقط کوتاه/بلند می‌شوند
export function edgeNormal(poly, i) {
  const a = poly[i];
  const b = poly[(i + 1) % poly.length];
  const u = unit(b[0] - a[0], b[1] - a[1]);
  return { u, n: [-u[1], u[0]] };
}
export function slideEdge(poly, i, s) {
  const N = poly.length;
  const ia = i;
  const ib = (i + 1) % N;
  const a = poly[ia];
  const b = poly[ib];
  const { u, n } = edgeNormal(poly, i);
  const a2 = [a[0] + n[0] * s, a[1] + n[1] * s];
  const pa = poly[(ia - 1 + N) % N];
  const nb = poly[(ib + 1) % N];
  const A = lineX(a2, u, pa, unit(a[0] - pa[0], a[1] - pa[1])) || a2;
  const B = lineX(a2, u, nb, unit(b[0] - nb[0], b[1] - nb[1])) || [b[0] + n[0] * s, b[1] + n[1] * s];
  const out = poly.slice();
  out[ia] = A;
  out[ib] = B;
  return out;
}

// گوشه‌ی i به Q؛ اگر یکی از لبه‌های کنارش روی دیوار است، گوشه روی همان دیوار سُر می‌خورد. همسایه‌ای که خودش
// روی دیوار است (سرِ یک لبه‌ی آزاد) هم روی دیوارش جابه‌جا می‌شود تا لبه‌ی آزاد هم‌جهت بماند (گوشه‌ی L صاف بماند)
export function moveCorner(poly, i, Q, fixed) {
  const N = poly.length;
  const prev = (i - 1 + N) % N;
  const next = (i + 1) % N;
  let q = Q;
  // سُر خوردن روی دیوار
  const along = fixed[prev] ? [poly[prev], poly[i]] : fixed[i] ? [poly[i], poly[next]] : null;
  if (along) {
    const [p1, p2] = along;
    const u = unit(p2[0] - p1[0], p2[1] - p1[1]);
    const t = (Q[0] - p1[0]) * u[0] + (Q[1] - p1[1]) * u[1];
    q = [p1[0] + u[0] * t, p1[1] + u[1] * t];
  }
  const out = poly.slice();
  out[i] = q;
  // همسایه‌هایی که روی دیوار هستند و با i یک لبه‌ی آزاد دارند
  [
    [prev, (prev - 1 + N) % N, !fixed[prev], fixed[(prev - 1 + N) % N]],
    [next, (next + 1) % N, !fixed[i], fixed[next]],
  ].forEach(([j, k, freeEdge, wallSide]) => {
    if (!freeEdge || !wallSide) return;
    const dirFree = unit(poly[j][0] - poly[i][0], poly[j][1] - poly[i][1]);
    const dirWall = unit(poly[k][0] - poly[j][0], poly[k][1] - poly[j][1]);
    const X = lineX(q, dirFree, poly[j], dirWall);
    if (X) out[j] = X;
  });
  return out;
}

// رأسِ تازه روی لبه‌ی i (برای ساختنِ شکلِ L یا پله)
export function insertVertex(poly, i, t = 0.5) {
  const a = poly[i];
  const b = poly[(i + 1) % poly.length];
  const out = poly.slice();
  out.splice(i + 1, 0, [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  return out;
}

// اگر کاربر مساحت داد و قسمت فقط یک لبه‌ی آزاد دارد: همان لبه جابه‌جا می‌شود تا مساحت درست شود
export function fitAreaBySlide(poly, i, target, inside) {
  const ok = (p) => p.every(([x, y]) => !inside || inside(x, y));
  const A0 = polyArea(poly);
  // جهتی که مساحت را زیاد می‌کند
  const dir = polyArea(slideEdge(poly, i, 1)) > A0 ? 1 : -1;
  let lo = 0;
  let hi = 0;
  if (target > A0) {
    hi = 50;
    while (hi < 5000 && polyArea(slideEdge(poly, i, dir * hi)) < target && ok(slideEdge(poly, i, dir * hi))) hi *= 1.6;
  } else {
    hi = -50;
    while (hi > -5000 && polyArea(slideEdge(poly, i, dir * hi)) > target && polyArea(slideEdge(poly, i, dir * hi)) > 1000) hi *= 1.6;
  }
  for (let it = 0; it < 40; it += 1) {
    const m = (lo + hi) / 2;
    const p = slideEdge(poly, i, dir * m);
    const a = polyArea(p);
    if ((target > A0 && a < target && ok(p)) || (target <= A0 && a > target)) lo = m;
    else hi = m;
  }
  const res = slideEdge(poly, i, dir * lo);
  return ok(res) ? res : poly;
}
